import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell, navForRole } from './components/AppShell';
import { ApprovalGate, useOperational } from './components/StoreStatus';
import { LockScreen, useAutoLock } from './components/LockScreen';
import { usePinMode, useCashier, useRole } from './lib/cashier';
import { useSession } from './lib/session';
import { autoConnectScale } from './lib/scale';
import { autoConnectPrinter } from './lib/printer';
import { pullStaff, startSyncLoop } from './lib/sync';
import { isLocalOnly } from './lib/local';
import { checkCloudUpgrade } from './lib/cloud-upgrade';
import { useLang } from './lib/i18n';
import { startPaymentAlerts } from './lib/paymentAlerts';
import { pushToNative } from './lib/soundbox';
import { Billing } from './pages/Billing';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { warmCatalog } from './lib/catalog';

/** Billing, Home and sign-in load with the app; other pages load on first visit (smaller start-up). */
type Loader = () => Promise<Record<string, unknown>>;
const loaders: Loader[] = [];
function page(name: string, load: Loader) {
  loaders.push(load);
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
}
const Admin = page('Admin', () => import('./pages/Admin'));
const Roles = page('Roles', () => import('./pages/Roles'));
const Customers = page('Customers', () => import('./pages/Customers'));
const DayClose = page('DayClose', () => import('./pages/DayClose'));
const PurchaseOrders = page('PurchaseOrders', () => import('./pages/PurchaseOrders'));
const Suppliers = page('Suppliers', () => import('./pages/Suppliers'));
const Fresh = page('Fresh', () => import('./pages/Fresh'));
const Transfers = page('Transfers', () => import('./pages/Transfers'));
const Inventory = page('Inventory', () => import('./pages/Inventory'));
const Offers = page('Offers', () => import('./pages/Offers'));
const Onboard = page('Onboard', () => import('./pages/Onboard'));
const Receive = page('Receive', () => import('./pages/Receive'));
const Reports = page('Reports', () => import('./pages/Reports'));
const Settings = page('Settings', () => import('./pages/Settings'));
const Setup = page('Setup', () => import('./pages/Setup'));
const Staff = page('Staff', () => import('./pages/Staff'));
const StockTake = page('StockTake', () => import('./pages/StockTake'));
const Transactions = page('Transactions', () => import('./pages/Transactions'));
const Soundbox = page('Soundbox', () => import('./pages/Soundbox'));

/** Once the till is idle, fetch the other pages too, so they open instantly and work offline (the service worker keeps them). */
function prefetchPages() {
  const idle = (window as unknown as { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback ?? ((f: () => void) => setTimeout(f, 1500));
  idle(() => { warmCatalog(); for (const l of loaders) void l().catch(() => {}); });
}

// Blank, not a spinner: pages are prefetched, so this shows for a frame at most and must not flash.
const PageLoading = () => <div className="h-60" />;

export function App() {
  const { status, init, me } = useSession();
  const operational = useOperational();
  const role = useRole();
  const pinMode = usePinMode();
  const active = useCashier((s) => s.active);
  // Switching language redraws every screen (keyed below) so each string is looked up again.
  const lang = useLang((s) => s.lang);
  useEffect(() => { void init(); }, [init]);
  useEffect(() => { if (status === 'ready') { startSyncLoop(); void pullStaff().catch(() => {}); void checkCloudUpgrade().catch(() => {}); startPaymentAlerts(); pushToNative(); void autoConnectScale(); void autoConnectPrinter(); prefetchPages(); } }, [status]);
  useAutoLock(status === 'ready' && !!pinMode);

  if (status === 'loading') return <div className="flex h-app items-center justify-center text-muted">Loading…</div>;
  if (status === 'signedOut') return <Login key={lang} />;
  if (status === 'onboarding') return <Suspense fallback={<PageLoading />}><Onboard key={lang} /></Suspense>;
  if (status === 'admin') return <Suspense fallback={<PageLoading />}><Admin /></Suspense>;
  if (pinMode === undefined) return null;
  if (pinMode && !active) return <LockScreen key={lang} />;
  // Pages this role may open in this store (defaults narrowed by the owner in Roles & access).
  const allowed = new Set(navForRole(role, me?.store.settings.rolePermissions, isLocalOnly(me)).map((n) => n.to));
  const guard = (path: string, el: ReactNode) => (allowed.has(path) ? el : <Navigate to="/" />);
  return (
    <BrowserRouter key={lang}>
      <AppShell>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/billing" element={guard('/billing', operational ? <Billing /> : <ApprovalGate />)} />
          <Route path="/day-close" element={guard('/day-close', operational ? <DayClose /> : <ApprovalGate />)} />
          <Route path="/customers" element={guard('/customers', <Customers />)} />
          <Route path="/inventory" element={guard('/inventory', <Inventory />)} />
          <Route path="/receive" element={guard('/receive', <Receive />)} />
          <Route path="/suppliers" element={guard('/suppliers', <Suppliers />)} />
          <Route path="/purchase-orders" element={guard('/purchase-orders', <PurchaseOrders />)} />
          <Route path="/fresh" element={guard('/fresh', <Fresh />)} />
          <Route path="/transfers" element={guard('/transfers', <Transfers />)} />
          <Route path="/stock-take" element={guard('/stock-take', <StockTake />)} />
          <Route path="/offers" element={guard('/offers', <Offers />)} />
          <Route path="/staff" element={guard('/staff', <Staff />)} />
          <Route path="/roles" element={guard('/roles', <Roles />)} />
          <Route path="/setup" element={role === 'owner' ? <Setup /> : <Navigate to="/" />} />
          <Route path="/transactions" element={guard('/transactions', <Transactions />)} />
          <Route path="/reports" element={guard('/reports', <Reports />)} />
          <Route path="/soundbox" element={guard('/soundbox', <Soundbox />)} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/:section" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
      </AppShell>
    </BrowserRouter>
  );
}
